import type {
  CustomerSummaryRecord,
  TaskCollectionRecord,
  TaskRecord,
  TaskStatus
} from '@shared/types';
import { placeTask, sortTasksByBoardOrder, taskStatusOrder } from '@shared/utils/tasks';

export interface TaskCardViewModel {
  id: string;
  title: string;
  description: string | null;
  dueLabel: string;
  labels: string[];
  statusLabel: string;
  statusTone: 'neutral' | 'warning' | 'info' | 'success';
  boardOrder: number;
  context: {
    href: string;
    icon: 'work' | 'customers';
    primaryLabel: string;
    secondaryLabel: string | null;
  } | null;
}

export interface TaskBoardColumnViewModel {
  status: TaskStatus;
  label: string;
  count: number;
  items: TaskCardViewModel[];
  isArchive: boolean;
}

export interface TaskCalendarGroupViewModel {
  label: string;
  items: TaskCardViewModel[];
}

function getTaskStatusTone(status: TaskStatus): TaskCardViewModel['statusTone'] {
  switch (status) {
    case 'todo':
      return 'warning';
    case 'in_progress':
      return 'info';
    case 'done':
      return 'success';
    default:
      return 'neutral';
  }
}

function matchesFilters(
  task: TaskRecord,
  options: {
    scopeFilter: 'all';
    labelFilter: 'all' | string;
    projectFilter: 'all' | string;
    query: string;
    labelById: Map<string, string>;
    projectById: Map<string, string>;
    customerById: Map<string, string>;
  }
): boolean {
  const matchesScope = options.scopeFilter === 'all';
  const matchesLabel = options.labelFilter === 'all' || task.taskLabelIds.includes(options.labelFilter);
  const matchesProject = options.projectFilter === 'all' || task.projectId === options.projectFilter;

  const lowerQuery = options.query.trim().toLowerCase();
  if (!lowerQuery) {
    return matchesScope && matchesLabel && matchesProject;
  }

  const labelText = task.taskLabelIds.map((id) => options.labelById.get(id) ?? '').join(' ');
  const projectText = task.projectId ? options.projectById.get(task.projectId) ?? '' : '';
  const customerText = task.customerId ? options.customerById.get(task.customerId) ?? '' : '';
  const haystack = `${task.title} ${task.description ?? ''} ${labelText} ${projectText} ${customerText}`.toLowerCase();

  return matchesScope && matchesLabel && matchesProject && haystack.includes(lowerQuery);
}

function formatDueLabel(locale: string, dueAt: string | null, noDeadlineLabel: string): string {
  if (!dueAt) {
    return noDeadlineLabel;
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(dueAt));
}

function truncateDescription(value: string | null): string | null {
  if (!value) {
    return null;
  }

  if (value.length <= 120) {
    return value;
  }

  return `${value.slice(0, 117)}...`;
}

function getCustomer(summaryList: CustomerSummaryRecord[], customerId: string | null) {
  return customerId ? summaryList.find((customer) => customer.id === customerId) ?? null : null;
}

function buildTaskCardViewModel(
  collection: TaskCollectionRecord,
  task: TaskRecord,
  options: {
    locale: string;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): TaskCardViewModel {
  const labels = task.taskLabelIds
    .map((taskLabelId) => collection.taskLabels.find((taskLabel) => taskLabel.id === taskLabelId)?.name ?? null)
    .filter((value): value is string => Boolean(value));
  const project = task.projectId ? collection.projectSummaries.find((summary) => summary.id === task.projectId) ?? null : null;
  const customer = getCustomer(collection.customerSummaries, project?.customerId ?? task.customerId);

  return {
    id: task.id,
    title: task.title,
    description: truncateDescription(task.description),
    dueLabel: formatDueLabel(options.locale, task.dueAt, options.t('common.noDeadline')),
    labels,
    statusLabel: options.t(`enums.taskStatus.${task.status}`),
    statusTone: getTaskStatusTone(task.status),
    boardOrder: task.boardOrder,
    context: project
      ? {
          href: `/projects/${project.id}`,
          icon: 'work',
          primaryLabel: project.name,
          secondaryLabel: customer?.name ?? null
        }
      : customer
        ? {
            href: `/customers/${customer.id}`,
            icon: 'customers',
            primaryLabel: customer.name,
            secondaryLabel: null
          }
        : null
  };
}

export function selectTaskLabelOptions(collection: TaskCollectionRecord, t: (key: string) => string) {
  return [
    { value: 'all', label: t('tasks.filters.allLabels') },
    ...collection.taskLabels.map((taskLabel) => ({
      value: taskLabel.id,
      label: taskLabel.name
    }))
  ];
}

export function selectTaskProjectOptions(collection: TaskCollectionRecord, t: (key: string) => string) {
  return [
    { value: 'all', label: t('tasks.filters.allProjects') },
    ...[...collection.projectSummaries]
      .sort((left, right) => left.name.localeCompare(right.name))
      .map((project) => ({
        value: project.id,
        label: project.name
      }))
  ];
}

export function selectTaskBoardColumns(
  collection: TaskCollectionRecord,
  options: {
    locale: string;
    scopeFilter: 'all';
    labelFilter: 'all' | string;
    projectFilter: 'all' | string;
    sort: 'recent';
    query: string;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): TaskBoardColumnViewModel[] {
  const labelById = new Map(collection.taskLabels.map((label) => [label.id, label.name] as const));
  const projectById = new Map(collection.projectSummaries.map((project) => [project.id, project.name] as const));
  const customerById = new Map(collection.customerSummaries.map((customer) => [customer.id, customer.name] as const));

  const visibleTasks = collection.tasks.filter((task) =>
    matchesFilters(task, {
      scopeFilter: options.scopeFilter,
      labelFilter: options.labelFilter,
      projectFilter: options.projectFilter,
      query: options.query,
      labelById,
      projectById,
      customerById
    })
  );

  return taskStatusOrder.map((status) => {
    const items = sortTasksByBoardOrder(visibleTasks.filter((task) => task.status === status)).map((task) =>
      buildTaskCardViewModel(collection, task, options)
    );

    return {
      status,
      label: options.t(`enums.taskStatus.${status}`),
      count: items.length,
      items,
      isArchive: status === 'archived'
    };
  });
}

export function selectTaskCalendarGroups(
  collection: TaskCollectionRecord,
  options: {
    locale: string;
    scopeFilter: 'all';
    labelFilter: 'all' | string;
    projectFilter: 'all' | string;
    sort: 'recent';
    query: string;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): TaskCalendarGroupViewModel[] {
  const labelById = new Map(collection.taskLabels.map((label) => [label.id, label.name] as const));
  const projectById = new Map(collection.projectSummaries.map((project) => [project.id, project.name] as const));
  const customerById = new Map(collection.customerSummaries.map((customer) => [customer.id, customer.name] as const));

  const visibleTasks = collection.tasks
    .filter((task) =>
      matchesFilters(task, {
        scopeFilter: options.scopeFilter,
        labelFilter: options.labelFilter,
        projectFilter: options.projectFilter,
        query: options.query,
        labelById,
        projectById,
        customerById
      })
    )
    .sort((left, right) => {
      if (!left.dueAt && !right.dueAt) {
        return left.boardOrder - right.boardOrder;
      }

      if (!left.dueAt) {
        return 1;
      }

      if (!right.dueAt) {
        return -1;
      }

      return new Date(left.dueAt).getTime() - new Date(right.dueAt).getTime();
    });

  const groups = new Map<string, TaskCardViewModel[]>();

  for (const task of visibleTasks) {
    const label = task.dueAt
      ? new Intl.DateTimeFormat(options.locale, {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }).format(new Date(task.dueAt))
      : options.t('tasks.calendar.noDeadline');
    const currentGroup = groups.get(label) ?? [];
    currentGroup.push(buildTaskCardViewModel(collection, task, options));
    groups.set(label, currentGroup);
  }

  return [...groups.entries()].map(([label, items]) => ({
    label,
    items
  }));
}

export function applyOptimisticTaskPlacement(
  collection: TaskCollectionRecord,
  options: {
    taskId: string;
    targetStatus: TaskStatus;
    targetBoardOrder: number;
    changedAt: string;
  }
): TaskCollectionRecord {
  return {
    ...collection,
    tasks: placeTask(collection.tasks, options)
  };
}
